import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'react-feather'
import { usePagination, useRowSelect, useTable } from 'react-table'
import { Checkbox } from '../../components'
import styleModule from './MobileTable.module.css'

const MobileTableRow = ({ withCheckbox, row }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className={`${styleModule['mobile-table-row']} ${expanded ? styleModule['expanded'] : styleModule['collapsed']} text-sm`}>
      <div className="flex">
        {withCheckbox && (
          <div className={styleModule['mobile-table-row__checkbox']}>
            <Checkbox {...row.getToggleRowSelectedProps()} />
          </div>
        )}

        <div className="w-full">
          <div className="flex flex-wrap -mx-2">
            {row.cells.slice(0, 2).map((cell, i) => (
              <MobileTableItem cell={cell} key={i} />
            ))}
          </div>

          <div className={`flex-wrap -mx-2 ${expanded ? 'flex' : 'hidden'}`}>
            {row.cells.slice(2).map((cell, i) => (
              <MobileTableItem cell={cell} key={i} />
            ))}
          </div>

          <button
            className={`flex justify-center items-center w-full focus:outline-none pb-2 ${styleModule['mobile-table-row__button']}`}
            onClick={() => setExpanded(!expanded)}
          >
            <ChevronDown />
          </button>
        </div>
      </div>
    </div>
  )
}

const MobileTableItem = ({ cell }) => (
  <div className="mb-4 px-2 w-1/2">
    <div className={`${styleModule['mobile-table-row__title']} text-gray-999`}>
      {cell.render('Header')}
    </div>
    <div className={styleModule['mobile-table-row__value']}>
      {cell.render('Cell')}
    </div>
  </div>
)

const MobileTable = ({ data, columns, withCheckbox, withPagination }) => {
  const {
    prepareRow,
    page,

    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data
    },
    usePagination,
    useRowSelect,
  )

  // Pagination
  const endSize = 1
  const midSize = 1
  const currentPage = pageIndex + 1
  const paginationNumbers = useMemo(
    () => {
      const numbers = []
      let dots = false

      for (let index = 1; index <= pageCount; index++) {
        if (index === currentPage) {
          numbers.push(index)
          dots = true
        } else {
          if (index <= endSize || (currentPage && index >= currentPage - midSize && index <= currentPage + midSize) || index > pageCount - endSize ) {
            numbers.push(index)
            dots = true
          } else if (dots) {
            numbers.push('elipsis')
            dots = false
          }
        }
      }

      return numbers
    },
    [currentPage, pageCount]
  )

  return (
    <div>
      <div className="mobile-table">
        <div className="mobile-table__body">
          {page.map((row, i) => {
            prepareRow(row)
            return <MobileTableRow withCheckbox={withCheckbox} row={row} key={i} />
          })}
        </div>
      </div>

      { /* Pagination */ }
      {withPagination && (
        <div className="flex justify-center text-gray-999 mt-8 lg:mt-16 mb-18">
          <button className="mx-3" onClick={() => previousPage()} disabled={!canPreviousPage}>
            <ChevronLeft/>
          </button>

          {paginationNumbers.map((number, index) => {
            if (number === 'elipsis') {
              return (
                <div className="mx-3" key={index}>
                  &hellip;
                </div>
              )
            } else {
              return (
                <button
                  className={`mx-3 ${number === currentPage ? 'text-primary' : ''}`}
                  onClick={() => gotoPage(number - 1)}
                  key={index}
                >
                  {number}
                </button>
              )
            }
          })}

          <button className="mx-3" onClick={() => nextPage()} disabled={!canNextPage}>
            <ChevronRight/>
          </button>
        </div>
      )}
    </div>
  )
}

MobileTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  withCheckbox: PropTypes.bool,
  withPagination: PropTypes.bool,
}

MobileTable.defaultProps = {
  withCheckbox: false,
  withPagination: true,
}

export { MobileTable }
